import "./FacilityFiltersComponent.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Field, FieldProps, Form, Formik, FormikProps, FormikValues} from "formik";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import FormikAutoCompleteDropdown
    from "../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import FormikDatePickerComponent from "../../../shared/components/core/formik-date-picker/FormikDatePickerComponent";
import {APIConfig} from "../../../constants";
import CommonService from "../../../shared/services/common.service";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import formValidationRules from "../../../shared/services/validation.service";
import * as Yup from "yup";


interface FacilityFiltersComponentProps {
    filtersData: any;
    onFiltersApply: (filterData: any) => void;
    clearAllFilters: () => void
}

const validationSchema = Yup.object().shape({
    startDate: Yup.mixed().nullable().when("endDate", {
        is: (endDate: Date) => {
            return Boolean(endDate)
        },
        then: Yup.mixed().nullable().required("Start date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    }),
    endDate: Yup.mixed().nullable().when("startDate", {
        is: (startDate: Date) => {
            return Boolean(startDate)
        },
        then: Yup.mixed().nullable().required("End date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    })
}, [['startDate', 'endDate']]);

const FacilityFiltersComponent = (props: FacilityFiltersComponentProps) => {
    const {filtersData, onFiltersApply, clearAllFilters} = props;
    const [dataFiltersInitialState, setDataFiltersInitialState] = useState(filtersData);
    const {
        facilityTypesList,
        userList,
        facilityStatusList
    } = useSelector((state: IRootReducerState) => state.staticData);
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const onFiltersApplyHandler = useCallback((values: any) => {
        const payload = {
            ...values
        }
        payload.types = payload.facilityTypesObjs.map((item: any) => item.code);
        payload.statuses = payload?.statusesObjs?.map((item: any) => item?.code);
        payload.cities = payload.citiesObjs.map((item: any) => item.city);
        payload.createdByIds = payload.createdByObjs.map((item: any) => item.id);
        payload.startDate = payload.startDate ? CommonService.convertDateFormat(values['startDate'], 'YYYY-MM-DD') : "";
        payload.endDate = payload.endDate ? CommonService.convertDateFormat(values['endDate'], 'YYYY-MM-DD') : "";

        onFiltersApply(payload);
    }, [onFiltersApply]);

    useEffect(() => {
        setDataFiltersInitialState(filtersData);
    }, [filtersData]);

    return (
        <div className={'facility-filters-component screen t-form-screen'}>
            <>
                <div className="drawer-sub-header horizontal-vertical-center-align">
                    <HeadingComponent title={"Filters"}/>
                    <div className={'filters-clear-all-button'}
                         onClick={() => {
                             formikRef.current && formikRef.current.resetForm()
                             clearAllFilters()
                         }}
                    >
                        Clear all
                    </div>
                </div>
                <div className="t-form t-form-drawer">
                    <Formik
                        initialValues={dataFiltersInitialState}
                        enableReinitialize={true}
                        innerRef={formikRef}
                        onSubmit={onFiltersApplyHandler}
                        validationSchema={validationSchema}
                    >
                        {({isSubmitting, values, isValid, setFieldValue, validateForm, errors, dirty}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [values, validateForm]);
                            return (
                                <Form noValidate={true} className={"facility-filters-holder"}>
                                    <div className="t-form-controls-wrapper">
                                        <Field name={'facilityTypesObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Facility Type'}
                                                        multiple={true}
                                                        data={facilityTypesList}
                                                        valueKey={"code"}
                                                        placeholder={'Select Facility Type'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.title) || '')}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'createdByObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Added by'}
                                                        multiple={true}
                                                        data={userList}
                                                        valueKey={"id"}
                                                        placeholder={'Select Added by'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.firstName + ' ' + option.lastName) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'citiesObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'City'}
                                                        placeholder={"start type for city"}
                                                        formikField={field}
                                                        url={APIConfig.CITY_LIST.URL}
                                                        method={APIConfig.CITY_LIST.METHOD}
                                                        searchMode={'serverSide'}
                                                        openOnFocus={true}
                                                        dataListKey={"data.docs"}
                                                        data={[]}
                                                        multiple={true}
                                                        valueKey={'city'}
                                                        getOptionLabel={(option => (option.city) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'statusesObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Facility Status'}
                                                        multiple={true}
                                                        data={facilityStatusList}
                                                        valueKey={"code"}
                                                        placeholder={'Select Facility Status'}
                                                        formikField={field}
                                                        getOptionLabel={(option) => (option.title)}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'startDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'Start Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'endDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'End Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="drawer-actions form-action-section">
                                        <ButtonComponent variant={"contained"}
                                                         fullWidth={true}
                                                         type={"submit"}
                                                         disabled={!isValid || !dirty}
                                        >
                                            Apply
                                        </ButtonComponent>
                                    </div>
                                </Form>
                            )
                        }
                        }
                    </Formik>
                </div>
            </>


        </div>
    );

};

export default FacilityFiltersComponent;
